import React, { useRef } from "react";
import {
  ChevronLeft,
  Clock,
  Zap,
  Dumbbell,
  SlidersHorizontal,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { t as translate, dateLocale } from "i18n";

const SESSION_TYPE_CONFIG = {
  match: {
    labelKey: "session.hero.types.match",
    bg: "#E0E7FF",
    color: "#4F46E5",
    Icon: Zap,
  },
  training: {
    labelKey: "session.hero.types.training",
    bg: "#FEF3C7",
    color: "#B45309",
    Icon: Dumbbell,
  },
  recovery: {
    labelKey: "session.hero.types.recovery",
    bg: "#CCFBF1",
    color: "#0F766E",
    Icon: Clock,
  },
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(dateLocale("en-GB"), {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end - start;
  if (diffMs <= 0) return null;
  const mins = Math.round(diffMs / 60000);
  if (mins < 60)
    return translate("session.hero.duration.minutes", { minutes: mins });
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0
    ? translate("session.hero.duration.hoursMinutes", {
        hours: hrs,
        minutes: remainMins,
      })
    : translate("session.hero.duration.hours", { hours: hrs });
};

const getDemoTitle = () => {
  const now = new Date();
  const weekday = now.toLocaleDateString(dateLocale("en-GB"), {
    weekday: "long",
  });
  const hour = now.getHours();
  let period;
  if (hour >= 5 && hour < 12) period = "morning";
  else if (hour >= 12 && hour < 17) period = "afternoon";
  else if (hour >= 17 && hour < 22) period = "evening";
  else period = "night";
  return translate("session.hero.demoTitle", {
    weekday,
    period: translate(`session.hero.periods.${period}`),
  });
};

const CONFIG_FIELDS = [
  {
    key: "storage_threshold_g",
    labelKey: "session.hero.config.storage",
    format: (v) => `${v}g`,
  },
  {
    key: "broadcast_threshold_g",
    labelKey: "session.hero.config.broadcast",
    format: (v) => `${v}g`,
  },
  {
    key: "header_threshold_g",
    labelKey: "session.hero.config.header",
    format: (v) => `${v}g`,
  },
  {
    key: "status_broadcast_interval_s",
    labelKey: "session.hero.config.statusInterval",
    format: (v) => `${v}s`,
  },
  {
    key: "broadcast_window_s",
    labelKey: "session.hero.config.broadcastWindow",
    format: (v) => `${v}s`,
  },
];

const SessionHeroSection = ({
  session,
  onBack,
  isDemoMode = false,
  onToggleDemoMode,
  onOpenDemoSettings,
}) => {
  const { t } = useTranslation();
  const longPressTimerRef = useRef(null);
  const wasLongPressRef = useRef(false);

  const startLongPress = () => {
    wasLongPressRef.current = false;
    if (!onOpenDemoSettings) return;
    longPressTimerRef.current = setTimeout(() => {
      wasLongPressRef.current = true;
      longPressTimerRef.current = null;
      onOpenDemoSettings();
    }, 700);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleLiveClick = () => {
    if (wasLongPressRef.current) {
      wasLongPressRef.current = false;
      return;
    }
    onToggleDemoMode?.();
  };
  const sessionType = session.type || session.session_type || "training";
  const typeConfig =
    SESSION_TYPE_CONFIG[sessionType] || SESSION_TYPE_CONFIG.training;
  const duration = formatDuration(session.start_time, session.end_time);

  return (
    <div className="px-6 pb-4 pt-5">
      {/* Back Navigation */}
      <button
        onClick={onBack}
        className="mb-4 flex cursor-pointer items-center gap-1 text-xs font-medium text-[#6B7280] transition duration-150 hover:text-[#374151]"
      >
        <ChevronLeft size={14} />
        {t("session.hero.backToTeam")}
      </button>

      {/* Session Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="mb-0.5 flex items-center gap-2">
            <h1 className="text-xl font-bold text-[#111827]">
              {isDemoMode
                ? getDemoTitle()
                : session.name ||
                  session.session_name ||
                  t("session.hero.untitled")}
            </h1>
            <span
              className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold"
              style={{
                backgroundColor: typeConfig.bg,
                color: typeConfig.color,
              }}
            >
              <typeConfig.Icon size={10} />
              {t(typeConfig.labelKey)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-[#6B7280]">
            <span>
              {formatDate(
                session.date || session.start_time || session.created_at
              )}
            </span>
            {duration && (
              <>
                <span className="text-[#D1D5DB]">&middot;</span>
                <span className="flex items-center gap-1">
                  <Clock size={11} />
                  {duration}
                </span>
              </>
            )}
            {session.status && (
              <>
                <span className="text-[#D1D5DB]">&middot;</span>
                <span className="capitalize">
                  {t(`session.hero.status.${session.status}`, {
                    defaultValue: session.status,
                  })}
                </span>
              </>
            )}
            {session.config != null && (
              <div className="group relative ml-1">
                <div className="flex h-6 w-6 cursor-default items-center justify-center rounded-md text-[#9CA3AF] transition duration-150 group-hover:bg-[#F3F4F6] group-hover:text-[#6B7280]">
                  <SlidersHorizontal size={13} />
                </div>
                <div className="invisible absolute left-0 top-full z-20 mt-1.5 min-w-[220px] rounded-xl border border-[#F3F4F6] bg-white p-3 shadow-lg group-hover:visible">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
                    {t("session.hero.deviceConfig")}
                  </p>
                  <div className="space-y-1.5">
                    {CONFIG_FIELDS.map((field) => {
                      const raw = session.config[field.key];
                      const display =
                        raw !== undefined && raw !== null
                          ? field.format(raw)
                          : "—";
                      return (
                        <div
                          key={field.key}
                          className="flex items-center justify-between gap-4"
                        >
                          <span className="text-[12px] text-[#6B7280]">
                            {t(field.labelKey)}
                          </span>
                          <span className="text-[12px] font-semibold text-[#111827]">
                            {display}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Live demo toggle — long-press to open settings */}
        {onToggleDemoMode && (
          <button
            onClick={handleLiveClick}
            onPointerDown={startLongPress}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
            onPointerCancel={cancelLongPress}
            onContextMenu={(e) => e.preventDefault()}
            className={`flex flex-shrink-0 select-none items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold transition duration-150 ${
              isDemoMode
                ? "bg-[#DCFCE7] text-[#16A34A] hover:bg-[#BBF7D0]"
                : "border border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
            }`}
          >
            {isDemoMode ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#16A34A]" />
                </span>
                {t("session.hero.live")}
              </>
            ) : (
              <>
                <span className="h-2 w-2 rounded-full bg-[#16A34A]" />
                {t("session.hero.goLive")}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SessionHeroSection;
